/**
 * What Timer til lønn shows per person, derived from Engine A's report rows
 * (GET /admin/report) and the period's shifts. The report is the export's own
 * source: both CSV files are written from the same `payroll` array, so every
 * krone here is the file's figure, not a second computation
 * (TF-CHROME-CORRECTIONS, 2026-09-15).
 *
 * Worked hours are not read from the report. aggregatePayroll
 * (admin/shiftController.js) gives an hour that matches a rate rule to that
 * rule's line and not to Regular, and two overlapping rules both receive the
 * same hour (backend/tests/payroll.test.js P15, P17), so neither Regular nor
 * the sum of line hours is the worked time. They are computed here the way
 * the report computes the hours it prices: each approved shift's punch
 * interval clipped to the period (backend/services/payrollMath.js
 * splitShiftIntoSegments), less the break as the report places it
 * (workedIn below). The date-range query answers a shift that starts or ends
 * inside the month, so a shift across a month boundary is on both months'
 * screens; whole-shift hours counted it twice (TF-ACCEPTANCE-CLOSURE).
 */
import type { ApiShift, ApiUser, ReportLine, ReportRow } from '@/api/data';
import { T } from '@/i18n';
import { oslo, osloISO } from '@/rules';
import { fullName } from '@/ui/format';
import { type BreakMode, type BreakPolicy, addDays } from '@timefly/core';
import { kindOf as closeKind } from '../timeliste/close';

/** The report puts the automatic break 3.5 h after punch in (backend/services/payrollMath.js:104). */
const BREAK_AT_MS = 3.5 * 3_600_000;
const ms = (v: string | null | undefined): number | null => {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
};

/** The report's period as the screen queries it: 'YYYY-MM-DD' first and last day, both included. */
export interface Period {
  from: string;
  to: string;
}
/** [start of `from`, start of the day after `to`) as instants, Oslo midnight both ends; the report's startOf('day') and endOf('day'). */
export const periodWindow = (p: Period): [number, number] => [
  Date.parse(osloISO(p.from, '00:00')),
  Date.parse(osloISO(addDays(p.to, 1), '00:00')),
];

/** The mode a shift is paid under: its own, stamped at punch-in, else the company's (plan 0032). */
const modeOfShift = (s: ApiShift, policy: BreakPolicy): BreakMode =>
  s.break_mode ?? policy.mode ?? (policy.auto ? 'auto' : 'punched');
/** The overlap in ms of the shift's recorded break with [from, to). */
const recordedIn = (s: ApiShift, from: number, to: number): number => {
  const p = ms(s.lunch_in_time);
  const q = ms(s.lunch_out_time);
  return p != null && q != null && q > p ? Math.max(0, Math.min(to, q) - Math.max(from, p)) : 0;
};

/** Worked hours of one shift inside [m0, m1): workedHours for that shift alone. */
export const workedIn = (s: ApiShift, policy: BreakPolicy, m0: number, m1: number): number =>
  workedHours([s], policy, m0, m1);

/**
 * Worked hours of one person's approved shifts inside [m0, m1), as the report counts them
 * (payrollMath.js splitShiftIntoSegments, applyLunchBreak, applyManualLunchBreak; processShifts in
 * admin/shiftController.js). The punch interval is clipped to the window. Each shift is counted under
 * its own break mode (plan 0032): a recorded break comes off by its overlap with the clipped interval;
 * the automatic break belongs to a stint, shifts back to back (16 to 00, then 00 to 06) being one, and
 * comes off once, 3.5 h after the stint starts, only in the window holding that mark (test C21); under
 * punched_min only what the stint's recorded breaks left short of it. A shift missing a punch, reversed,
 * or outside the window is 0.
 */
export function workedHours(shifts: ApiShift[], policy: BreakPolicy, m0: number, m1: number): number {
  const timed = shifts
    .map((s) => ({ s, a: ms(s.punch_in_time), b: ms(s.punch_out_time), mode: modeOfShift(s, policy) }))
    .filter(
      (x): x is { s: ApiShift; a: number; b: number; mode: BreakMode } =>
        x.a != null && x.b != null && x.b > x.a,
    )
    .sort((x, y) => x.a - y.a);
  const stints: { a: number; b: number; inside: number; recorded: number }[] = [];
  let total = 0;
  for (const x of timed) {
    const from = Math.max(x.a, m0);
    const to = Math.min(x.b, m1);
    const inside = Math.max(0, to - from);
    total += inside - (x.mode === 'auto' || inside === 0 ? 0 : recordedIn(x.s, from, to));
    if (x.mode === 'punched') continue;
    const recorded = x.mode === 'punched_min' ? recordedIn(x.s, x.a, x.b) : 0;
    const last = stints[stints.length - 1];
    if (last && x.a <= last.b) {
      last.b = Math.max(last.b, x.b);
      last.inside += inside;
      last.recorded += recorded;
    } else stints.push({ a: x.a, b: x.b, inside, recorded });
  }
  for (const st of stints) {
    const at = st.a + BREAK_AT_MS;
    if (st.b - st.a > policy.breakFromHours * 3_600_000 && at >= m0 && at < m1)
      total -= Math.min(Math.max(0, policy.autoMinutes * 60_000 - st.recorded), st.inside);
  }
  return total / 3_600_000;
}

export type LineKind = NonNullable<ReportLine['kind']>;

/**
 * The server decides the kind where it builds the line
 * (admin/shiftController.js buildPayrollReportObject). An older server sends
 * none: then "Regular" is the base line, a line carrying `type` (a rate rule's
 * calculation_type) is a supplement, and anything else is absence. Overtime is
 * never guessed without `kind`; `type` is "extra_per_hour" or "percent" for
 * supplements and overtime alike.
 */
export const kindOf = (name: string, l: ReportLine): LineKind =>
  l.kind ?? (name === 'Regular' ? 'base' : l.type ? 'supplement' : 'absence');

export interface PayLine {
  u: ApiUser;
  /** kr on the base line ("Regular"): every approved hour at the hourly rate (D17 B), less hours banked for avspasering (plan 0027 G2); none for a salaried person (D35 A). */
  base: number;
  /** kr on supplement lines, the tillegg alone, as the file shows them (D17 B). */
  supp: number;
  ot: number;
  /** kr on the absence lines; paid absence is at the hourly rate, unpaid is 0 kr with its hours still counted. */
  absence: number;
  absenceHours: number;
  /** of absenceHours, the hours after the employer's days: the "(NAV)" lines, 0 kr */
  navHours: number;
  /** sick and sykt barn lines are counted in days since D18 (2026-09-22); the NAV share apart */
  absenceDays: number;
  navDays: number;
  /** lines whose dagsats rests on under three months of work in Timefly */
  shortBasis: number;
  /** sick lines by the reason code the report gives (under_4_weeks, start_unknown, no_plan, nav_on_call, over_6g) */
  sickNotes: Record<string, number>;
  /** Sum of salary_total over every line, which is the generic file's "TOTAL Salary" row. */
  total: number;
  /** Hours after breaks over the approved shifts inside the period, the company's policy applied as the report applies it; null until the company has loaded. */
  approvedH: number | null;
  unapproved: number;
  open: number;
  unpunched: number;
  /** this store's shifts behind unapproved, open and unpunched, so a check can name each one (#264) */
  due?: { id: number; date: string; kind: 'unapproved' | 'open' | 'unpunched' }[];
  monthly: boolean;
  /**
   * Every line as the report gave it, so the page can show the arithmetic instead of a total.
   *
   * The designer, 2026-09-21: "jeigu yra, pvz., 10 val. x 15,74 kr, taip ir turėtų aiškiai
   * matytis lønnslippe... dabar rodo kažką kaip 204,79 + 15 kr, ir gaunasi neaišku, kiek
   * tiksliai buvo valandų prie timelønn." The hours and the rate were on every line already and
   * were being summed away before the page ever saw them.
   */
  parts: PayPart[];
}

export interface PayPart {
  name: string;
  kind: LineKind;
  hours: number;
  /** kr per hour on this line (per day when unit is 'dager'). The report's own figure, not one derived by division. */
  rate: number;
  total: number;
  unit?: 'dager';
  /** the lønnsartnummer the file writes for this line; '' when the line has none */
  code: string;
  /** 'nav' on the "(NAV)" line: the days after the employer's, 0 kr from the company */
  paidBy?: 'nav';
  /** the report's reasons for this line (LINE_NOTES in run.ts) */
  notes?: string[];
}

/** Another store's shifts in a report over several (plan 0031), under that store's break policy. */
export interface OtherStore {
  shifts: ApiShift[];
  policy: BreakPolicy | null;
}

export function payLines(
  users: ApiUser[],
  rows: ReportRow[],
  shifts: ApiShift[],
  day0: string,
  policy: BreakPolicy | null,
  period: Period,
  // the page passes Date.now(); the default keeps the tests at a fixed moment, midnight UTC of day0
  now: number = Date.parse(`${day0}T00:00:00Z`),
  // the other chosen stores: their hours and counts join each person's, each store's break its own,
  // as the report counts them (plan 0031 slice 2)
  others: OtherStore[] = [],
): PayLine[] {
  const byUser = new Map(rows.map((r) => [Number(r.id), r]));
  const [m0, m1] = periodWindow(period);
  return users
    .map((u) => {
      const sums: Record<LineKind, number> = { base: 0, supplement: 0, overtime: 0, absence: 0 };
      const parts: PayPart[] = [];
      let absenceHours = 0;
      let navHours = 0;
      let absenceDays = 0;
      let navDays = 0;
      let shortBasis = 0;
      const sickNotes: Record<string, number> = {};
      let total = 0;
      for (const [name, l] of Object.entries(byUser.get(u.id)?.totals ?? {})) {
        const kind = kindOf(name, l);
        sums[kind] += l.salary_total;
        total += l.salary_total;
        const days = l.unit === 'dager';
        if (kind === 'absence') {
          if (days) absenceDays += l.hours;
          else absenceHours += l.hours;
        }
        if (l.paid_by === 'nav') {
          if (days) navDays += l.hours;
          else navHours += l.hours;
        }
        if (l.basis_short) shortBasis += 1;
        for (const n of l.notes ?? []) sickNotes[n] = (sickNotes[n] ?? 0) + 1;
        // the same words the Tripletex Kommentar carries (CV1)
        const refund = l.refund_days
          ? ` (${T('{n, plural, one {# dag} other {# dager}} refusjon fra NAV', { n: l.refund_days })})`
          : '';
        parts.push({
          name: name + refund,
          kind,
          hours: l.hours,
          rate: l.rate ?? 0,
          total: l.salary_total,
          code: l.salary_type_code ?? '',
          ...(days ? { unit: 'dager' as const } : {}),
          ...(l.paid_by ? { paidBy: l.paid_by } : {}),
          ...(l.notes?.length ? { notes: l.notes } : {}),
        });
      }
      // biggest first: on a payslip the line somebody queries is usually the largest one
      parts.sort((a, b) => b.total - a.total || b.hours - a.hours);
      const mine = shifts.filter((s) => s.user_id === u.id);
      const theirs = others.map((o) => ({ ...o, shifts: o.shifts.filter((s) => s.user_id === u.id) }));
      // what each shift is comes from the rule Timeliste, Hjem and Innboks use (timeliste/close.ts)
      // counted in the month the shift starts, as Timeliste shows it: a night shift from the 31st
      // is approved with its own month, even though its hours after midnight are paid in this one
      const kinds = [...mine, ...theirs.flatMap((o) => o.shifts)]
        .filter((s) => {
          const d = oslo(s.start_time)?.date ?? '';
          return d >= period.from && d <= period.to;
        })
        .map((s) => closeKind(s, now));
      return {
        u,
        base: sums.base,
        supp: sums.supplement,
        ot: sums.overtime,
        absence: sums.absence,
        absenceHours,
        navHours,
        absenceDays,
        navDays,
        shortBasis,
        sickNotes,
        total,
        approvedH:
          policy && theirs.every((o) => o.policy)
            ? [{ shifts: mine, policy }, ...theirs].reduce(
                (h, o) =>
                  h +
                  workedHours(
                    o.shifts.filter((s) => s.is_approved),
                    o.policy as BreakPolicy,
                    m0,
                    m1,
                  ),
                0,
              )
            : null,
        unapproved: kinds.filter((k) => k === 'ready' || k === 'deviation').length,
        open: kinds.filter((k) => k === 'open').length,
        unpunched: kinds.filter((k) => k === 'missing').length,
        due: mine.flatMap((s) => {
          const date = oslo(s.start_time)?.date ?? '';
          if (date < period.from || date > period.to) return [];
          const k = closeKind(s, now);
          const kind: NonNullable<PayLine['due']>[number]['kind'] | null =
            k === 'open'
              ? 'open'
              : k === 'missing'
                ? 'unpunched'
                : k === 'ready' || k === 'deviation'
                  ? 'unapproved'
                  : null;
          return kind ? [{ id: s.id, date, kind }] : [];
        }),
        monthly: u.contract_type === 'monthly',
        parts,
      };
    })
    .sort((a, b) => fullName(a.u).localeCompare(fullName(b.u), 'nb'));
}
